import React, { useState } from 'react';
import Card from '../common/Card';
import Avatar from '../common/Avatar';
import { useAuth } from '../../contexts/AuthContext';
import { useLanguage } from '../../contexts/LanguageContext';
import { formatRelativeTime } from '../../utils/helpers';

const CommentsList = ({ comments, postId, onAddComment, onRefresh }) => {
  const { isAdmin, isDesigner, isWriter } = useAuth();
  const { isRTL } = useLanguage();
  const [newComment, setNewComment] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isInternal, setIsInternal] = useState(false);
  const [showInternal, setShowInternal] = useState(true);

  // Only team members can create/view internal comments
  const canCreateInternal = isAdmin || isDesigner || isWriter;

  // Filter general comments (not pinned to image)
  const allGeneralComments = comments.filter(
    (comment) => comment.position_x === null || comment.position_y === null
  );

  // Filter visible comments based on internal flag and user role
  const generalComments = allGeneralComments.filter((comment) => {
    if (comment.is_internal) {
      return showInternal && canCreateInternal;
    }
    return true; // Public comments are always visible
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!newComment.trim()) return;

    try {
      setIsSubmitting(true);
      await onAddComment({
        comment_text: newComment,
        position_x: null,
        position_y: null,
        is_internal: isInternal && canCreateInternal,
      });
      setNewComment('');
      setIsInternal(false);
    } catch (error) {
      console.error('Failed to add comment:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Count comments
  const publicCommentsCount = allGeneralComments.filter(c => !c.is_internal).length;
  const internalCommentsCount = allGeneralComments.filter(c => c.is_internal).length;

  return (
    <Card className="p-6 h-full">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-lg font-semibold text-gray-900">
          {isRTL ? 'التعليقات' : 'Comments'} ({generalComments.length})
        </h2>
        {canCreateInternal && internalCommentsCount > 0 && (
          <label className="flex items-center gap-2 text-sm text-gray-600 cursor-pointer">
            <input
              type="checkbox"
              checked={showInternal}
              onChange={(e) => setShowInternal(e.target.checked)}
              className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
            />
            <span>{isRTL ? 'إظهار الداخلية' : 'Show Internal'}</span>
          </label>
        )}
      </div>

      {/* Comments List */}
      <div className="space-y-4 mb-6 max-h-96 overflow-y-auto scrollbar-thin">
        {generalComments.length === 0 ? (
          <p className="text-gray-500 text-sm text-center py-8">
            {isRTL ? 'لا توجد تعليقات بعد. كن أول من يعلق!' : 'No comments yet. Be the first to comment!'}
          </p>
        ) : (
          generalComments.map((comment) => (
            <div key={comment.id} className="flex space-x-3">
              <Avatar
                name={comment.user_name}
                imageUrl={comment.avatar_url}
                size="sm"
              />
              <div className="flex-1 min-w-0">
                <div className={`flex items-center ${isRTL ? 'space-x-reverse' : 'space-x-2'} mb-1 flex-wrap gap-2`}>
                  <p className="text-sm font-medium text-gray-900">
                    {comment.user_name}
                  </p>
                  <span className="text-xs text-gray-500 capitalize">
                    {comment.user_role}
                  </span>
                  {comment.is_internal && (
                    <span className="px-2 py-0.5 bg-purple-100 text-purple-800 text-xs rounded-full font-medium flex items-center gap-1">
                      <span>🔒</span>
                      <span>{isRTL ? 'داخلي' : 'Internal'}</span>
                    </span>
                  )}
                </div>
                <p className={`text-sm mb-1 ${comment.is_internal ? 'text-gray-600 italic' : 'text-gray-700'}`}>
                  {comment.comment_text}
                </p>
                <p className="text-xs text-gray-500">
                  {formatRelativeTime(comment.created_at)}
                </p>
                {comment.is_resolved && (
                  <span className="inline-block mt-1 px-2 py-0.5 bg-green-100 text-green-800 text-xs rounded">
                    ✓ Resolved
                  </span>
                )}
              </div>
            </div>
          ))
        )}
      </div>

      {/* Add Comment Form */}
      <form onSubmit={handleSubmit} className="border-t border-gray-200 pt-4">
        <textarea
          value={newComment}
          onChange={(e) => setNewComment(e.target.value)}
          placeholder={isRTL ? 'أضف تعليقاً...' : 'Add a comment...'}
          className="textarea text-sm"
          rows="3"
          disabled={isSubmitting}
        />
        {canCreateInternal && (
          <label className="flex items-center gap-2 text-sm text-gray-600 mt-2 cursor-pointer">
            <input
              type="checkbox"
              checked={isInternal}
              onChange={(e) => setIsInternal(e.target.checked)}
              className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
            />
            <span className="flex items-center gap-1">
              <span>🔒</span>
              <span>{isRTL ? 'تعليق داخلي (للفريق فقط)' : 'Internal comment (team only)'}</span>
            </span>
          </label>
        )}
        <button
          type="submit"
          disabled={!newComment.trim() || isSubmitting}
          className="btn btn-primary w-full mt-2 text-sm"
        >
          {isSubmitting ? (isRTL ? 'جاري الإرسال...' : 'Posting...') : (isRTL ? 'إرسال التعليق' : 'Post Comment')}
        </button>
      </form>
    </Card>
  );
};

export default CommentsList;


